import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import api from '../utils/api';

const MIN_LOADING_TIME = 150;

export function useCourses(studentId) {
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  // Initialize loading to true if studentId exists to prevent flash of empty content
  const [loading, setLoading] = useState(!!studentId);
  const [error, setError] = useState(null);
  const prevStudentIdRef = useRef(studentId);

  // Synchronously set loading state when studentId changes (before paint)
  useLayoutEffect(() => {
    if (studentId !== prevStudentIdRef.current) {
      if (studentId) {
        setLoading(true);
        setCourses([]);
        setSemesters([]);
      } else {
        setLoading(false);
        setCourses([]);
        setSemesters([]);
      }
      prevStudentIdRef.current = studentId;
    }
  }, [studentId]);

  useEffect(() => {
    if (!studentId) {
      return;
    }

    const fetchCourses = async () => {
      const startTime = Date.now();
      try {
        const response = await api.get(`/parent/children/${studentId}/courses`);

        const elapsed = Date.now() - startTime;
        const remaining = MIN_LOADING_TIME - elapsed;

        if (remaining > 0) {
          await new Promise(resolve => setTimeout(resolve, remaining));
        }

        setCourses(response.data.courses || []);
        setSemesters(response.data.semesters || []);
        setError(null);
      } catch (err) {
        const elapsed = Date.now() - startTime;
        const remaining = MIN_LOADING_TIME - elapsed;

        if (remaining > 0) {
          await new Promise(resolve => setTimeout(resolve, remaining));
        }

        setError(err.response?.data?.message || 'Failed to fetch courses');
        setCourses([]);
        setSemesters([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [studentId]);

  return { courses, semesters, loading, error };
}
